import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import { randomUUID } from '@/shared/lib/uuid';

export type ContractStatus = 'draft' | 'active' | 'completed' | 'cancelled' | 'defaulted';
export type Frequency = 'monthly' | 'quarterly';
export type ScheduleStatus = 'pending' | 'partial' | 'paid' | 'overdue' | 'waived';

export type Contract = {
  id: string;
  contractNumber: string;
  projectId: string;
  apartmentId: string;
  customerId: string;
  reservationId: string | null;
  status: ContractStatus;
  totalAmount: string; // tiyin
  downPayment: string;
  installmentCount: number;
  frequency: Frequency;
  firstDueDate: string;
  lateFeePercentMonthly: string;
  gracePeriodDays: number;
  language: 'ru' | 'uz' | 'en';
  contractPdfUrl: string | null;
  signedDate: string | null;
  activatedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  notes: string | null;
  discountAmount: string;
  discountReason: string | null;
  createdAt: string;
  updatedAt: string;
  customer?: { id: string; firstName: string; lastName: string; middleName?: string | null; phone: string; email?: string | null };
  apartment?: { id: string; fullCode: string; number?: string; areaSqm: string };
};

export type PaymentSchedule = {
  id: string;
  contractId: string;
  installmentNumber: number;
  isDownPayment: boolean;
  dueDate: string;
  amountDue: string;
  amountPaid: string;
  status: ScheduleStatus;
  lateFeeAccrued: string;
  lateFeeWaived: string;
  paidAt: string | null;
};

export type PreviewScheduleInput = {
  totalAmount: string;
  downPayment: string;
  installmentCount: number;
  frequency: Frequency;
  firstDueDate: string;
};

export type PreviewScheduleRow = {
  installmentNumber: number;
  isDownPayment: boolean;
  dueDate: string;
  amountDue: string;
};

export type CreateContractDraftInput = {
  apartmentId: string;
  customerId: string;
  reservationId?: string;
  totalAmount: string;
  downPayment: string;
  installmentCount: number;
  frequency: Frequency;
  firstDueDate: string;
  lateFeePercentMonthly: number;
  gracePeriodDays: number;
  language: 'ru' | 'uz' | 'en';
  documentTemplateId?: string;
  signedDate?: string;
  notes?: string;
  discountAmount?: string;
  discountReason?: string;
};

export function useContractsList(params?: { customerId?: string; status?: ContractStatus; projectId?: string; search?: string }) {
  return useQuery({
    queryKey: ['contracts', params],
    queryFn: async () => {
      const res = await api.get<{ data: Contract[]; pagination: { hasMore: boolean } }>('/contracts', { params });
      return res.data;
    },
  });
}

export function useContract(id: string | undefined) {
  return useQuery({
    queryKey: ['contract', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ data: Contract & { schedules?: PaymentSchedule[] } }>(`/contracts/${id}`);
      return res.data.data;
    },
  });
}

export function useContractSchedule(id: string | undefined) {
  return useQuery({
    queryKey: ['contract-schedule', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ data: PaymentSchedule[] }>(`/contracts/${id}/schedule`);
      return res.data.data;
    },
  });
}

export function usePreviewSchedule() {
  return useMutation({
    mutationFn: async (input: PreviewScheduleInput) => {
      const res = await api.post<{ data: PreviewScheduleRow[] }>('/contracts/preview-schedule', input);
      return res.data.data;
    },
  });
}

export function useCreateContractDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateContractDraftInput) => {
      const res = await api.post<{ data: Contract }>('/contracts/draft', input, {
        headers: { 'Idempotency-Key': randomUUID() },
      });
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contracts'] });
    },
  });
}

export function useActivateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ data: Contract }>(`/contracts/${id}/activate`);
      return res.data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['contracts'] });
      void qc.invalidateQueries({ queryKey: ['contract', data.id] });
      void qc.invalidateQueries({ queryKey: ['apartments-grid'] });
    },
  });
}

export function useCancelContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await api.post<{ data: Contract }>(`/contracts/${id}/cancel`, { reason });
      return res.data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['contracts'] });
      void qc.invalidateQueries({ queryKey: ['contract', data.id] });
      void qc.invalidateQueries({ queryKey: ['apartments-grid'] });
    },
  });
}

export type ContractAmendment = {
  id: string;
  contractId: string;
  version: number;
  reason: string;
  effectiveFrom: string;
  changes: Record<string, unknown>;
  amendmentPdfUrl: string | null;
  createdById: string;
  createdAt: string;
};

export function useContractAmendments(id: string | undefined) {
  return useQuery({
    queryKey: ['contract-amendments', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ data: ContractAmendment[] }>(`/contracts/${id}/amendments`);
      return res.data.data;
    },
  });
}

export function useCreateAmendment(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reason: string; effectiveFrom: string; changes: Record<string, unknown> }) => {
      const res = await api.post<{ data: ContractAmendment }>(`/contracts/${contractId}/amendments`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contract-amendments', contractId] });
    },
  });
}

export function useRegenerateContractPdf() {
  return useMutation({
    mutationFn: async (args: { id: string; templateId?: string }) => {
      const res = await api.post<{ data: { key: string } }>(
        `/contracts/${args.id}/regenerate-pdf`,
        args.templateId ? { templateId: args.templateId } : {},
      );
      return res.data.data;
    },
  });
}
